import React, { useState, useEffect } from "react";
import { Button, Select, Tooltip, message, Modal } from "antd";
import {
  DragOutlined,
  ArrowUpOutlined,
  ArrowDownOutlined,
  SwapOutlined,
  InfoCircleOutlined,
  CheckCircleFilled
} from "@ant-design/icons";
import GetCascadeChildPlansApi from "../api/plans/GetCascadeChildPlansApi";
import UpdateHierarchyOrderApi from "../api/plans/UpdateHierarchyOrderApi";
import MovePlanToParentApi from "../api/plans/MovePlanToParentApi";
import GetAvailableParentsApi from "../api/plans/GetAvailableParentsApi";
import { openNotificationSuccess, openNotificationError } from "../helpers/NotificationHelpers";

const { Option } = Select;

function PlanHierarchyManager({ planId, onHierarchyChange }) {
  const [childPlans, setChildPlans] = useState([]);
  const [loading, setLoading] = useState(false);
  const [moveModalVisible, setMoveModalVisible] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [availableParents, setAvailableParents] = useState([]);
  const [selectedParent, setSelectedParent] = useState(null);

  useEffect(() => {
    if (planId) {
      loadChildPlans();
    }
  }, [planId]);

  const loadChildPlans = async () => {
    setLoading(true);
    try {
      const response = await GetCascadeChildPlansApi(planId);
      if (response.message === "success" && response.data) {
        // Sort by orderIndex
        const sortedPlans = response.data.sort((a, b) => (a.orderIndex || 0) - (b.orderIndex || 0));
        console.log('Loaded cascade children:', sortedPlans);
        setChildPlans(sortedPlans);
      } else {
        message.error("Failed to load child plans");
      }
    } catch (error) {
      console.error("Error loading child plans:", error);
      message.error("Failed to load child plans");
    } finally {
      setLoading(false);
    }
  };

  const movePlanUp = async (planId, currentIndex) => {
    if (currentIndex === 0) return; // Already at top

    const newOrderIndex = currentIndex - 1;
    await updatePlanOrder(planId, newOrderIndex);
  };

  const movePlanDown = async (planId, currentIndex) => {
    if (currentIndex === childPlans.length - 1) return; // Already at bottom

    const newOrderIndex = currentIndex + 1;
    await updatePlanOrder(planId, newOrderIndex);
  };

  const updatePlanOrder = async (planId, newOrderIndex) => {
    try {
      const response = await UpdateHierarchyOrderApi(planId, newOrderIndex);
      if (response.message === "success") {
        openNotificationSuccess("bottomRight", "Plan order updated successfully");
        await loadChildPlans(); // Reload to reflect changes
        if (onHierarchyChange) {
          onHierarchyChange();
        }
      } else {
        openNotificationError("bottomRight", response.message || "Failed to update plan order");
      }
    } catch (error) {
      console.error("Error updating plan order:", error);
      openNotificationError("bottomRight", "Failed to update plan order");
    }
  };

  const showMoveModal = (plan) => {
    setSelectedPlan(plan);
    setSelectedParent(null);
    setMoveModalVisible(true);
    // Load available parent options (excluding current plan and its descendants)
    loadAvailableParents(plan.planId);
  };

  const loadAvailableParents = async (excludePlanId) => {
    try {
      const response = await GetAvailableParentsApi(excludePlanId);
      if (response.message === "success" && response.data) {
        // Filter out the current plan and its descendants
        const filteredParents = response.data
          .filter(item => item.planId !== excludePlanId)
          .map(item => ({
            id: item.planId,
            title: item.plan?.title || 'Untitled Plan'
          }));

        // Add root level option
        setAvailableParents([
          { id: null, title: "Root Level (No Parent)" },
          ...filteredParents
        ]);
      } else {
        setAvailableParents([
          { id: null, title: "Root Level (No Parent)" }
        ]);
      }
    } catch (error) {
      console.error("Error loading available parents:", error);
      setAvailableParents([
        { id: null, title: "Root Level (No Parent)" }
      ]);
    }
  };

  const handleMovePlan = async () => {
    if (!selectedPlan) return;

    try {
      const response = await MovePlanToParentApi(selectedPlan.planId, selectedParent);
      if (response.message === "success") {
        openNotificationSuccess("bottomRight", "Plan moved successfully");
        setMoveModalVisible(false);
        await loadChildPlans(); // Reload to reflect changes
        if (onHierarchyChange) {
          onHierarchyChange();
        }
      } else {
        openNotificationError("bottomRight", response.message || "Failed to move plan");
      }
    } catch (error) {
      console.error("Error moving plan:", error);
      openNotificationError("bottomRight", "Failed to move plan");
    }
  };

  const getHierarchyTypeColor = (type) => {
    return type === 'CASCADE' ? '#28a745' : '#ffc107';
  };

  const getHierarchyTypeText = (type) => {
    return type === 'CASCADE' ? 'Cascade' : 'Split';
  };

  if (loading) {
    return <div className="text-center py-4">Loading hierarchy...</div>;
  }

  if (childPlans.length === 0) {
    return (
      <div className="text-center py-4 text-gray-500">
        No child plans found
      </div>
    );
  }

  return (
    <div className="plan-hierarchy-manager">
      <div className="mb-4">
        <h4 className="text-lg font-medium mb-2">Child Plans Hierarchy</h4>
        <p className="text-sm text-gray-600 mb-4">
          Manage the order and hierarchy of child plans. Use the arrows to reorder plans within the same level.
        </p>
      </div>

      <div className="space-y-2">
        {childPlans.map((plan, index) => (
          <div
            key={plan.planId || plan.id || index}
            className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
          >
            <div className="flex items-center space-x-3 flex-1">
              <div className="text-gray-400">
                <DragOutlined />
              </div>

              <div className="flex-1">
                <div className="flex items-center space-x-2">
                  <span className="font-medium text-gray-900">
                    {plan.title || plan.plan?.title || 'Untitled Plan'}
                  </span>
                  {(plan.finalized === 1 || plan.finalized === "1" || plan.finalized === true) && (
                    <CheckCircleFilled
                      style={{
                        color: '#90EE90',
                        marginLeft: '8px',
                        fontSize: '16px'
                      }}
                      title="Plan Finalized"
                    />
                  )}
                  <span
                    className="px-2 py-1 text-xs font-medium rounded"
                    style={{
                      backgroundColor: '#28a745' + '20',
                      color: '#28a745'
                    }}
                  >
                    Cascade
                  </span>
                  <span className="text-xs text-gray-500">
                    Level {plan.hierarchyLevel || 1}
                  </span>
                </div>
                <div className="text-sm text-gray-600">
                  Position: {(plan.orderIndex !== null && plan.orderIndex !== undefined) ? plan.orderIndex + 1 : index + 1} of {childPlans.length}
                </div>
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <Tooltip title="Move up">
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowUpOutlined />}
                  disabled={index === 0}
                  onClick={() => movePlanUp(plan.planId || plan.id, index)}
                />
              </Tooltip>

              <Tooltip title="Move down">
                <Button
                  type="text"
                  size="small"
                  icon={<ArrowDownOutlined />}
                  disabled={index === childPlans.length - 1}
                  onClick={() => movePlanDown(plan.planId || plan.id, index)}
                />
              </Tooltip>

              <Tooltip title="Move to different parent">
                <Button
                  type="text"
                  size="small"
                  icon={<SwapOutlined />}
                  onClick={() => showMoveModal(plan)}
                />
              </Tooltip>
            </div>
          </div>
        ))}
      </div>

      {/* Move Plan Modal */}
      <Modal
        title="Move Plan to Different Parent"
        open={moveModalVisible}
        onOk={handleMovePlan}
        onCancel={() => setMoveModalVisible(false)}
        okText="Move Plan"
        cancelText="Cancel"
        okButtonProps={{ disabled: !selectedParent }}
      >
        {selectedPlan && (
          <div className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-2">
                Moving: <strong>{selectedPlan.title || selectedPlan.plan?.title || 'Untitled Plan'}</strong>
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select New Parent
              </label>
              <Select
                placeholder="Choose a parent plan"
                className="w-full"
                value={selectedParent}
                onChange={setSelectedParent}
              >
                {availableParents.map((parent) => (
                  <Option key={parent.id || 'null'} value={parent.id}>
                    {parent.title}
                  </Option>
                ))}
              </Select>
            </div>

            <div className="bg-blue-50 p-3 rounded-lg">
              <div className="flex items-start space-x-2">
                <InfoCircleOutlined className="text-blue-500 mt-0.5" />
                <div className="text-sm text-blue-700">
                  <p className="font-medium">Note:</p>
                  <ul className="list-disc list-inside mt-1 space-y-1">
                    <li>Moving a plan will update its hierarchy level</li>
                    <li>The plan will be reordered within its new parent</li>
                    <li>This action cannot be undone</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

export default PlanHierarchyManager; 